// src/utils/printLayout.js
//
// Layout padrão de impressão da Arcus (A4).
// Abre uma janela com o documento formatado e dispara a impressão (o usuário pode salvar em PDF).
// Usado por: Relatório de Cobranças (Financeiro) e Relatório de Atendimento (Chamado).

export const escapeHtml = (v) => String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Texto livre → HTML seguro (mantém quebras de linha e converte **negrito**)
export const textToHtml = (v) => escapeHtml(v)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');

export const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

export const toDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    if (v.seconds) return new Date(v.seconds * 1000);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};
export const fmtDate = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('pt-BR') : '—'; };
export const fmtDateTime = (v) => {
    const d = toDate(v);
    return d ? `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '—';
};

const BASE_CSS = `
    @page {
        size: A4;
        margin: 14mm 14mm 18mm 14mm;
        @bottom-left { content: "Arcus Informática"; font: 600 8pt 'Segoe UI', system-ui, sans-serif; color: #94a3b8; }
        @bottom-right { content: "Página " counter(page) " de " counter(pages); font: 600 8pt 'Segoe UI', system-ui, sans-serif; color: #94a3b8; }
    }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; background: #e2e8f0; }
    body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1e293b; font-size: 10pt; line-height: 1.45; }
    .page { background: #fff; max-width: 210mm; margin: 16px auto; padding: 14mm; box-shadow: 0 10px 30px rgba(15,23,42,.15); }
    @media print {
        html, body { background: #fff; }
        .page { margin: 0; padding: 0; box-shadow: none; max-width: none; }
        .no-print { display: none !important; }
    }

    /* Barra de ações (só na tela) */
    .toolbar { position: sticky; top: 0; z-index: 10; display: flex; justify-content: center; gap: 8px; padding: 10px; background: #0f172a; }
    .toolbar button { font: 700 12px 'Segoe UI', sans-serif; letter-spacing: .5px; text-transform: uppercase; border: 0; border-radius: 8px; padding: 9px 18px; cursor: pointer; }
    .toolbar .primary { background: #06b6d4; color: #fff; }
    .toolbar .ghost { background: #334155; color: #e2e8f0; }

    /* Cabeçalho */
    .doc-header { display: flex; justify-content: space-between; align-items: center; gap: 24px; padding-bottom: 14px; border-bottom: 3px solid #06b6d4; }
    .doc-header img { height: 46px; display: block; }
    .doc-title { text-align: right; }
    .doc-title .kicker { font-size: 8pt; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: #06b6d4; }
    .doc-title h1 { margin: 2px 0 0; font-size: 17pt; font-weight: 800; color: #0f172a; letter-spacing: -.3px; }
    .doc-title .sub { margin-top: 2px; font-size: 9pt; color: #64748b; }
    .doc-meta { display: flex; justify-content: space-between; gap: 12px; margin: 8px 0 18px; font-size: 8pt; color: #94a3b8; }

    /* Blocos */
    .section { margin-top: 18px; break-inside: avoid; }
    .section-title { display: flex; align-items: center; gap: 8px; margin: 0 0 8px; font-size: 8.5pt; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: #0f172a; }
    .section-title::before { content: ''; width: 4px; height: 12px; border-radius: 2px; background: #06b6d4; }
    .box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; background: #f8fafc; }
    .muted { color: #94a3b8; }
    .mono { font-family: 'Cascadia Mono', Consolas, 'Courier New', monospace; }
    .right { text-align: right; }
    .center { text-align: center; }

    /* Grade de informações */
    .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .info-grid .cell { padding: 8px 12px; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; min-width: 0; }
    .info-grid .cell:nth-child(3n) { border-right: 0; }
    .info-grid .cell.wide { grid-column: span 3; border-right: 0; }
    .label { display: block; font-size: 7pt; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #94a3b8; margin-bottom: 1px; }
    .value { font-weight: 600; color: #0f172a; word-break: break-word; }

    /* Selos */
    .pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 7.5pt; font-weight: 800; letter-spacing: .5px; text-transform: uppercase; border: 1px solid currentColor; white-space: nowrap; }

    /* Tabelas */
    table { width: 100%; border-collapse: collapse; font-size: 9pt; }
    thead th { background: #0f172a; color: #fff; font-size: 7.5pt; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 7px 8px; text-align: left; }
    thead th:first-child { border-top-left-radius: 6px; }
    thead th:last-child { border-top-right-radius: 6px; }
    tbody td { padding: 7px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
    tbody tr:nth-child(even) td { background: #f8fafc; }
    tr { break-inside: avoid; }
    tfoot td { padding: 8px; font-weight: 800; }
    .group-row td { background: #ecfeff !important; color: #0e7490; font-weight: 800; font-size: 8.5pt; border-bottom: 1px solid #a5f3fc; }
    .subtotal-row td { background: #fff !important; font-weight: 700; color: #334155; border-bottom: 2px solid #cbd5e1; }

    /* Totais */
    .totals { margin-left: auto; margin-top: 8px; width: 260px; }
    .totals .row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 9pt; color: #475569; }
    .totals .grand { margin-top: 4px; padding: 8px 12px; border-radius: 8px; background: #0f172a; color: #fff; font-size: 11pt; font-weight: 800; }
    .totals .grand span:last-child { color: #34d399; }

    /* Cards de resumo */
    .cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
    .card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; border-top: 3px solid var(--c); }
    .card .label { color: var(--c); }
    .card .big { font-size: 13pt; font-weight: 800; color: #0f172a; }
    .card .small { font-size: 8pt; color: #64748b; }

    /* Linha do tempo */
    .timeline { border-left: 2px solid #e2e8f0; margin-left: 6px; padding-left: 16px; }
    .tl-item { position: relative; margin-bottom: 10px; break-inside: avoid; }
    .tl-item::before { content: ''; position: absolute; left: -22px; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: #06b6d4; border: 2px solid #fff; box-shadow: 0 0 0 1px #06b6d4; }
    .tl-item.internal::before { background: #f59e0b; box-shadow: 0 0 0 1px #f59e0b; }
    .tl-head { display: flex; justify-content: space-between; gap: 8px; font-size: 8pt; color: #64748b; }
    .tl-head b { color: #0f172a; }
    .tl-body { margin-top: 3px; padding: 8px 10px; border-radius: 6px; background: #f8fafc; border: 1px solid #e2e8f0; font-size: 9pt; }
    .tl-item.internal .tl-body { background: #fffbeb; border-color: #fde68a; }
    .tag-internal { font-size: 6.5pt; font-weight: 800; letter-spacing: 1px; color: #b45309; background: #fef3c7; border-radius: 4px; padding: 1px 5px; margin-left: 4px; }

    /* Assinaturas */
    .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 46px; break-inside: avoid; }
    .signatures div { border-top: 1px solid #334155; padding-top: 6px; text-align: center; font-size: 8.5pt; color: #475569; }
    .signatures b { display: block; color: #0f172a; }

    .doc-footer { margin-top: 26px; padding-top: 10px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 7.5pt; color: #94a3b8; }
`;

/**
 * Abre a janela de impressão com o layout padrão.
 * @param {object} opts
 * @param {string} opts.docTitle   Título da aba / nome sugerido do PDF
 * @param {string} opts.kicker     Texto pequeno acima do título (ex.: "Financeiro")
 * @param {string} opts.title      Título do documento
 * @param {string} [opts.subtitle] Linha abaixo do título
 * @param {string} opts.bodyHtml   Conteúdo (HTML já montado e escapado)
 * @param {string} [opts.footerNote] Texto do rodapé à esquerda
 * @param {string} [opts.extraCss]
 * @param {string} [opts.emittedBy] E-mail de quem emitiu
 */
/**
 * Monta o HTML completo do documento no layout padrão.
 * modo 'print' (padrão): com barra de ações e impressão automática.
 * modo 'pdf': sem barra/script, página branca de largura A4 (usado para gerar PDF no navegador).
 */
export const buildPrintHtml = ({ docTitle, kicker, title, subtitle = '', bodyHtml, footerNote = 'Documento gerado pela Intranet Arcus', extraCss = '', emittedBy = '' }, { modo = 'print' } = {}) => {
    const logoUrl = `${window.location.origin}/logo_full.png`;
    const user = emittedBy;
    const now = new Date();
    const emitted = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    const pdf = modo === 'pdf';
    const pdfCss = pdf ? `
    html, body { background: #fff; }
    .page { margin: 0; box-shadow: none; max-width: none; width: 210mm; padding: 12mm; }
    ` : '';

    return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(docTitle || title)}</title>
<style>${BASE_CSS}${extraCss}${pdfCss}</style>
</head>
<body>
    ${pdf ? '' : `<div class="toolbar no-print">
        <button class="primary" onclick="window.print()">Imprimir / Salvar PDF</button>
        <button class="ghost" onclick="window.close()">Fechar</button>
    </div>`}
    <div class="page">
        <header class="doc-header">
            <img src="${logoUrl}" alt="Arcus Informática" />
            <div class="doc-title">
                ${kicker ? `<div class="kicker">${escapeHtml(kicker)}</div>` : ''}
                <h1>${escapeHtml(title)}</h1>
                ${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ''}
            </div>
        </header>
        <div class="doc-meta">
            <span>Emitido em ${emitted}${user ? ` por ${escapeHtml(user)}` : ''}</span>
            <span>arcusinformatica.com.br</span>
        </div>
        ${bodyHtml}
        <footer class="doc-footer">
            <span>${escapeHtml(footerNote)}</span>
        </footer>
    </div>
    ${pdf ? '' : `<script>
        (function () {
            var img = document.querySelector('.doc-header img');
            var go = function () { setTimeout(function () { window.focus(); window.print(); }, 250); };
            if (!img || img.complete) { go(); } else { img.onload = go; img.onerror = go; }
        })();
    </script>`}
</body>
</html>`;
};

/** Abre a janela de impressão com o layout padrão (mesmos parâmetros de buildPrintHtml). */
export const openPrintDocument = (opts) => {
    const win = window.open('', '_blank');
    if (!win) return false; // pop-up bloqueado
    win.document.open();
    win.document.write(buildPrintHtml(opts));
    win.document.close();
    return true;
};

/**
 * Gera um PDF (Blob) a partir do layout padrão, sem abrir janela.
 * Renderiza num iframe oculto e captura com html2canvas + jsPDF.
 */
export const gerarPdfDoLayout = async (opts) => {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:800px;height:1200px;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    try {
        const idoc = iframe.contentDocument;
        idoc.open();
        idoc.write(buildPrintHtml(opts, { modo: 'pdf' }));
        idoc.close();
        // Espera imagens (logo, QR Code) e fontes
        const imgs = Array.from(idoc.images || []);
        await Promise.all(imgs.map(img => (img.complete ? Promise.resolve() : new Promise(r => { img.onload = r; img.onerror = r; }))));
        if (idoc.fonts && idoc.fonts.ready) { try { await idoc.fonts.ready; } catch (e) { /* ignora */ } }
        await new Promise(r => setTimeout(r, 150));

        const pageEl = idoc.querySelector('.page');
        const canvas = await html2canvas(pageEl, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false, windowWidth: 800 });
        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
        const pageW = 210, pageH = 297;
        const imgH = (canvas.height * pageW) / canvas.width;
        const img = canvas.toDataURL('image/jpeg', 0.92);
        if (imgH <= pageH + 0.5) {
            pdf.addImage(img, 'JPEG', 0, 0, pageW, imgH);
        } else {
            // Conteúdo maior que uma página: fatia em várias
            let y = 0;
            while (y < imgH - 0.5) {
                if (y > 0) pdf.addPage();
                pdf.addImage(img, 'JPEG', 0, -y, pageW, imgH);
                y += pageH;
            }
        }
        pdf.setProperties({ title: opts.docTitle || opts.title || 'Documento', creator: 'Intranet Arcus' });
        return pdf.output('blob');
    } finally {
        iframe.remove();
    }
};
